import { useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { filterUrl } from './consts';

const CFB_SPORT_ID = 1;
const CBB_SPORT_ID = 2;
const NFL_SPORT_ID = 3;

// The record/units of a single breakdown bucket.
type Rec = { w: number; l: number; p: number; u: number };

const ZERO: Rec = { w: 0, l: 0, p: 0, u: 0 };

function toNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Sum a stat node's `.total` across its (possibly period-nested) shape. */
function totalOf(node: unknown): number {
  if (node == null || typeof node !== 'object') {
    return toNum(node);
  }
  const obj = node as Record<string, { total?: number }>;
  if (typeof obj.total !== 'undefined' && typeof obj.total !== 'object') {
    return toNum(obj.total);
  }
  let amt = 0;
  for (const k in obj) {
    amt += toNum(obj[k]?.total);
  }
  return amt;
}

type BreakNode = {
  chunk?: string | number;
  stats?: { win?: unknown; loss?: unknown; push?: unknown };
  units?: number;
};

/** Find a breakdown node by its `chunk` value within a stats group (keys vary). */
function findChunk(
  group: Record<string, BreakNode> | undefined,
  chunk: string,
): BreakNode | undefined {
  if (!group) {
    return undefined;
  }
  return Object.values(group).find((n) => `${n?.chunk}` === chunk);
}

/** Record + units of a breakdown node (playType/period/gameType/seasonId). */
function recOf(node?: BreakNode): Rec {
  if (!node?.stats) {
    return { ...ZERO };
  }
  return {
    w: totalOf(node.stats.win),
    l: totalOf(node.stats.loss),
    p: totalOf(node.stats.push),
    u: toNum(node.units),
  };
}

/** The overall record (all graded plays) from the top-level result rollup. */
function overallRec(stats?: API.ReleaseStats): Rec {
  const r = stats?.result;
  if (!r) {
    return { ...ZERO };
  }
  return {
    w: toNum(r.win?.total),
    l: toNum(r.loss?.total),
    p: toNum(r.push?.total),
    u: toNum(r.win?.units) + toNum(r.loss?.units),
  };
}

const add = (a: Rec, b: Rec): Rec => ({ w: a.w + b.w, l: a.l + b.l, p: a.p + b.p, u: a.u + b.u });
const sub = (a: Rec, b: Rec): Rec => ({ w: a.w - b.w, l: a.l - b.l, p: a.p - b.p, u: a.u - b.u });
const plays = ({ w, l, p }: Rec) => w + l + p;

/** "227-160" — the trailing "-0" (no pushes) is dropped (#2004). */
export function formatRecord({ w, l, p }: Rec): string {
  return `${w}-${l}${p ? `-${p}` : ''}`;
}

function winPct({ w, l }: Rec): string {
  const denom = w + l;
  return denom ? `${((w / denom) * 100).toFixed(1)}%` : '—';
}

const unitsStr = (u: number) => `${u > 0 ? '+' : ''}${u.toFixed(1)}u`;

type DimensionKey = 'markets' | 'betType' | 'season';

type Segment = {
  key: string;
  label: string;
  note?: string;
  rec: Rec;
  href?: string;
  // A season's phase rows. When present they REPLACE this row in the list (the combined
  // season would just duplicate them); the combined total still shows in the header.
  phases?: Segment[];
};

// Season phases (releases.seasonType), newest first to match the newest-season-first
// list: within a split season, regular season reads above preseason.
const PHASES: { type: API.SeasonTypes; label: string }[] = [
  { type: 'all-star-game', label: 'all-star game' },
  { type: 'summer-league', label: 'summer league' },
  { type: 'postseason', label: 'postseason' },
  { type: 'regular-season', label: 'regular season' },
  { type: 'preseason', label: 'preseason' },
];

/**
 * A season's per-phase rows, from the `seasonPhase` rollup (keyed "<seasonId>:<phase>").
 *
 * A season is split only when it has preseason plays AND at least one other phase (NFL
 * today), so a preseason's record always reads separately from the regular season's
 * instead of being blended into it. The phases replace the combined season row in the
 * list and sum to it, since both come from the same season join. Postseason, when
 * present, is its own phase. Seasons without a preseason are unchanged.
 */
export function seasonPhaseSegments(
  stats: API.ReleaseStats | undefined,
  seasonId: string | number,
  sportLabel?: string,
): Segment[] {
  const group = stats?.seasonPhase;
  if (!group) {
    return [];
  }
  const phases = PHASES.map(({ type, label }) => ({
    key: `${seasonId}:${type}`,
    label: sportLabel ? `${sportLabel} ${label}` : label.charAt(0).toUpperCase() + label.slice(1),
    rec: recOf(findChunk(group as never, `${seasonId}:${type}`)),
  })).filter((s) => plays(s.rec) > 0);

  const hasPreseason = phases.some((s) => s.key.endsWith(':preseason'));
  return hasPreseason && phases.length > 1 ? phases : [];
}

/**
 * The "Markets" segments for the selected sport (#2004).
 *
 * The split that matters is market tier — the standard market vs the softer,
 * lower-limit one — but each sport expresses that differently:
 *   NFL  preseason is the soft market; regular season and postseason price alike, so
 *        they are combined.
 *   CFB  FCS games and "extra" games (one FBS vs one FCS team) both carry lowered bet
 *        minimums, so they group against the full FBS market.
 *   CBB  no subdivisions, but "extra" is the same idea — typically smaller D1 schools.
 *
 * Each soft bucket is summed and the standard tier is derived as overall minus soft, so
 * this never depends on a 'normal' chunk being present in the aggregate.
 */
function marketSegments(stats: API.ReleaseStats | undefined, sportId: number | null): Segment[] {
  if (!stats || sportId === null) {
    return [];
  }
  const overall = overallRec(stats);

  if (sportId === NFL_SPORT_ID) {
    const pre = recOf(findChunk(stats.seasonType as never, 'preseason'));
    if (plays(pre) === 0) {
      return [];
    }
    return [
      {
        key: 'regular',
        label: 'Regular season',
        note: 'incl. postseason',
        rec: sub(overall, pre),
      },
      { key: 'preseason', label: 'Preseason', rec: pre },
    ];
  }

  if (sportId === CFB_SPORT_ID || sportId === CBB_SPORT_ID) {
    const fcs = recOf(findChunk(stats.gameType as never, 'fcs'));
    const extra = recOf(findChunk(stats.gameType as never, 'extra'));
    const soft = add(fcs, extra);
    if (plays(soft) === 0) {
      return [];
    }
    const isCfb = sportId === CFB_SPORT_ID;
    return [
      { key: 'standard', label: isCfb ? 'FBS' : 'D1', rec: sub(overall, soft) },
      {
        key: 'soft',
        label: isCfb ? 'FCS & extra' : 'Extra',
        note: 'lowered limits',
        rec: soft,
      },
    ];
  }

  return [];
}

// playType / period chunk -> label, for the "Bet type" dimension.
//
// The two groups deliberately overlap: `playType` spans every period and `period` spans
// every play type, so a 1st-half total counts under BOTH "Totals" and "1st half". These
// rows are two views of the same plays, not a partition of them, so they never sum to
// the overall record.
const BET_TYPE_LABELS: { group: 'playType' | 'period'; chunk: string; label: string }[] = [
  { group: 'playType', chunk: 'side', label: 'Sides' },
  { group: 'playType', chunk: 'total', label: 'Totals' },
  { group: 'period', chunk: '1', label: '1st half' },
  { group: 'period', chunk: '2', label: '2nd half' },
  { group: 'playType', chunk: 'prop', label: 'Props' },
  { group: 'playType', chunk: 'future', label: 'Futures' },
];

/**
 * The tabbed breakdown card (#2004): Markets · Bet type · By season, rendered as compact
 * text rows (label, record, win %, units) so a ~30-season list stays short on mobile.
 */
// How many seasons the "By season" list shows before "Show all" (#2142).
const SEASON_PREVIEW_ROWS = 5;

export function BreakdownTabs({
  stats,
  baseUrl,
  isSingleSport = false,
  sportId = null,
}: {
  stats?: API.ReleaseStats;
  baseUrl: string;
  // Retained for the call site; the card's visibility now follows whether it has
  // segments to show, not the request's loading flag.
  isLoading?: boolean;
  isSingleSport?: boolean;
  // The single numeric sportId when one sport is selected, else null.
  sportId?: number | null;
}) {
  // `null` = follow the preferred default; a click pins an explicit dimension.
  const [dimension, setDimension] = useState<DimensionKey | null>(null);
  // The last dimension we actually rendered, so a mid-load gap can't bounce the card
  // onto a different (much taller) dimension and back.
  const lastResolved = useRef<DimensionKey | null>(null);
  const [showAllSeasons, setShowAllSeasons] = useState(false);

  const markets = useMemo(() => marketSegments(stats, sportId), [stats, sportId]);

  const betTypes = useMemo<Segment[]>(() => {
    if (!stats) {
      return [];
    }
    return BET_TYPE_LABELS.map(({ group, chunk, label }) => ({
      key: `${group}-${chunk}`,
      label,
      rec: recOf(findChunk(stats[group] as never, chunk)),
    })).filter((s) => plays(s.rec) > 0);
  }, [stats]);

  const seasons = useMemo<Segment[]>(() => {
    if (!stats?.seasonId) {
      return [];
    }
    return Object.values(stats.seasonId)
      .filter((row) => !row.meta?.season?.[row.chunk]?.hidden)
      .map((row) => {
        const season = row.meta?.season?.[row.chunk];
        const sportLabel = row.meta?.sport?.[season?.sportId || 0]?.shortName || undefined;
        return {
          key: `${row.chunk}`,
          label: season?.name || '—',
          note: sportLabel,
          rec: recOf(row),
          href: filterUrl(baseUrl, season?.sportId, season?.id, season?.name),
          phases: seasonPhaseSegments(stats, row.chunk, sportLabel).map((phase) => ({
            ...phase,
            label: season?.name || '—',
            note: phase.label,
            href: filterUrl(baseUrl, season?.sportId, season?.id, season?.name),
          })),
        };
      })
      .filter((s) => plays(s.rec) > 0)
      .sort((a, b) => b.label.localeCompare(a.label, 'en'));
  }, [stats, baseUrl]);

  const dimensions: { key: DimensionKey; label: string; rows: Segment[]; show: boolean }[] = [
    { key: 'markets', label: 'Markets', rows: markets, show: markets.length > 1 },
    {
      key: 'betType',
      label: 'Bet type',
      rows: betTypes,
      show: isSingleSport && betTypes.length > 1,
    },
    { key: 'season', label: 'By season', rows: seasons, show: seasons.length > 0 },
  ];
  const visible = dimensions.filter((d) => d.show);

  // Lead with Markets (the "where does the edge come from" view) rather than the season
  // list, which runs long. Resolution is STICKY: on a sport change the stats arrive in
  // pieces, so for a beat Markets isn't derivable yet and a plain `visible[0]` fell
  // through to the ~30-row season list — the card ballooned and snapped back, which
  // read as the whole thing remounting. Holding the last resolved dimension keeps it
  // steady across that gap (#2004).
  const explicit = dimension ? visible.find((d) => d.key === dimension) : undefined;
  const sticky = explicit
    ? undefined
    : visible.find((d) => d.key === lastResolved.current) || undefined;
  const active = explicit || sticky || visible[0];
  if (active) {
    lastResolved.current = active.key;
  }

  // Render ONLY when there is something to break down. Holding the slot open while a
  // sport change is in flight looked right in theory, but the target sport often has no
  // segments at all (e.g. CBB under My results for an account with no CBB plays), so the
  // card mounted a skeleton and unmounted it again within ~8ms — a flash directly under
  // the header. Appearing once, when it has content, is one clean transition (#2004).
  if (visible.length === 0) {
    return null;
  }

  const rows = active?.rows || [];
  // Only the season list needs bounding — it runs ~30 deep. Markets (2 rows) and Bet
  // type (6 at most) are bounded by construction. It used to be capped in a nested
  // scroll box, which on a phone is a scroll inside a scroll; instead show the most
  // recent seasons and let the member expand the rest in place (#2142).
  const capped = active?.key === 'season' && rows.length > SEASON_PREVIEW_ROWS;
  // The cap counts seasons, then a split season expands into its phase rows (both always
  // shown, never one without the other).
  const shownRows = (capped && !showAllSeasons ? rows.slice(0, SEASON_PREVIEW_ROWS) : rows).flatMap(
    (r) => (r.phases?.length ? r.phases : [r]),
  );

  return (
    <div className="mb-4 rounded-2xl border border-zinc-950/[0.08] bg-white p-2 shadow-xs dark:border-white/10 dark:bg-zinc-900">
      <div className="flex min-h-[2.125rem] gap-1 border-zinc-950/[0.06] border-b px-1 pb-2 dark:border-white/[0.07]">
        {visible.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => setDimension(d.key)}
            aria-pressed={active?.key === d.key}
            className={twMerge(
              'rounded-lg px-3 py-1.5 font-semibold text-xs',
              active?.key === d.key
                ? 'bg-zinc-100 text-zinc-900 dark:bg-white/10 dark:text-white'
                : 'text-zinc-400 hover:text-zinc-700 dark:text-zinc-500 dark:hover:text-zinc-200',
            )}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* The season list starts collapsed so one dimension can't make the card several
          times taller than the others and jump the whole page on every switch. */}
      <ul className="flex flex-col divide-y divide-zinc-950/[0.06] p-1 dark:divide-white/[0.06]">
        {shownRows.map((row) => (
          <SegmentRow key={row.key} segment={row} />
        ))}
      </ul>
      {capped && (
        <button
          type="button"
          onClick={() => setShowAllSeasons((v) => !v)}
          aria-expanded={showAllSeasons}
          className="mx-1 mb-1 w-[calc(100%-0.5rem)] rounded-lg py-2 font-semibold text-xs text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800 dark:text-zinc-400 dark:hover:bg-white/5 dark:hover:text-white"
        >
          {showAllSeasons ? 'Show fewer seasons' : `Show all ${rows.length} seasons`}
        </button>
      )}
    </div>
  );
}

/** One breakdown row: label + record/win% on the left, units on the right. */
function SegmentRow({ segment }: { segment: Segment }) {
  const { rec } = segment;

  const content = (
    <div className="flex items-baseline justify-between gap-3">
      <div className="min-w-0 truncate">
        <span className="font-semibold text-sm text-zinc-900 dark:text-white">{segment.label}</span>
        {segment.note && (
          <span className="ml-1.5 text-[11px] text-zinc-400 dark:text-zinc-500">
            {segment.note}
          </span>
        )}
        <span className="ml-2 text-xs text-zinc-500 tabular-nums dark:text-zinc-400">
          {formatRecord(rec)} · {winPct(rec)}
        </span>
      </div>
      <span
        data-sign={rec.u > 0 ? 'positive' : rec.u < 0 ? 'negative' : 'zero'}
        className={twMerge(
          'shrink-0 whitespace-nowrap font-bold text-sm tabular-nums',
          rec.u > 0 && 'text-emerald-600 dark:text-emerald-400',
          rec.u < 0 && 'text-rose-600 dark:text-rose-400',
          rec.u === 0 && 'text-zinc-500 dark:text-zinc-400',
        )}
      >
        {unitsStr(rec.u)}
      </span>
    </div>
  );

  const className =
    'block rounded-lg px-2 py-2 transition hover:bg-zinc-50 dark:hover:bg-white/[0.03]';

  return (
    <li>
      {segment.href ? (
        <a href={segment.href} className={className}>
          {content}
        </a>
      ) : (
        <div className={className}>{content}</div>
      )}
    </li>
  );
}
